import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useEmployeesByIds } from "@/queries/employees/use-employees.query.ts";
import { ApiReaction } from "@/types/common.types.ts";
import {
  Box,
  Button,
  EmojiDisplay,
  FontAwesomeIcon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface ReactionsProps {
  reactions: ApiReaction[];
  save?: (emoji: string, isNew: boolean) => Promise<boolean>;
}

export function Reactions({ reactions, save }: ReactionsProps) {
  const [hoveredReaction, setHoveredReaction] = useState<number[]>([]);
  const { data: usersLoaded, isLoading: isEmployeesLoading } = useEmployeesByIds(hoveredReaction);
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();

  const handleChangeReaction = useCallback(
    async (emoji: string) => {
      const hasEmoji = reactions?.find((reaction) => reaction.emoji === emoji)?.is_mine ?? false;

      await save?.(emoji, !hasEmoji);
    },
    [reactions]
  );

  const handleMouseEnter = useCallback(
    (emoji: string) => {
      setHoveredReaction(
        reactions.find((reaction) => reaction.emoji === emoji)?.employee_ids ?? []
      );
    },
    [reactions]
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredReaction([]);
  }, [reactions]);

  return (
    <Stack items={"start"} gap={"sm"} direction={"horizontal"} className="flex-wrap">
      {reactions.map((reaction) => (
        <Tooltip delay={false} key={`reaction-${reaction.emoji}`}>
          <TooltipTrigger>
            <Button
              onMouseEnter={() => handleMouseEnter(reaction.emoji)}
              onMouseLeave={handleMouseLeave}
              variant={reaction.is_mine ? "primary" : "secondary"}
              onClick={async () => {
                await handleChangeReaction(reaction.emoji);
              }}
            >
              <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
                <EmojiDisplay size={"md"} id={reaction.emoji} />
                <Text
                  className={twMerge(
                    "font-normal",
                    reaction.is_mine ? "text-light" : "text-dark/60"
                  )}
                >
                  {reaction.count}
                </Text>
              </Stack>
            </Button>
          </TooltipTrigger>
          <TooltipContent className={"max-w-40"}>
            <Stack gap={"sm"} items={"center"} className="p-2">
              <EmojiDisplay size={"lg"} id={reaction.emoji} />

              <Box className={"text-center text-sm"}>
                {isEmployeesLoading
                  ? t("common:loading")
                  : usersLoaded?.map((employee) => renderEmployeeName(employee)).join(", ")}
              </Box>
            </Stack>
          </TooltipContent>
        </Tooltip>
      ))}
      <EmojiPickerDropdown onEmojiSelect={handleChangeReaction}>
        <Button
          variant={"secondary"}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-face-smile-plus" />}
        ></Button>
      </EmojiPickerDropdown>
    </Stack>
  );
}
